import React, { Suspense, lazy } from 'react';
import { compose } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import reduxConnectContainer from './FeaturedPost.container';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PremiumFolderDataIcon from '@wix/da-shared-react/pkg/GallectionFolderDropdown/_partials/PremiumFolderDataIcon';
import { isDeviationAuthor } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import WidgetBox, { Props as WidgetBoxProps } from '../_partials/WidgetBox';
import FavouritesLabel from '../_partials/FavouritesLabel';
import { default as FeaturedPost, Props as WidgetProps } from './FeaturedPost';
import EmptyState from '../../EmptyState';
import ChunkLoadingPopover from '../_partials/ChunkLoadingPopover';

import s from './FeaturedPost.scss';

const PostPicker = lazy(
  () =>
    import(
      /* webpackChunkName: "owner" */ './EditForm/PostPicker/PostPicker.connected'
    )
);

const EditForm = lazy(
  () => import(/* webpackChunkName: "owner" */ './EditForm')
);

const enhance = compose(reduxConnectContainer, withTranslation());
export interface State {
  showPostPicker: boolean;
}
class FeaturedPostBox extends React.Component<
  WidgetProps & WidgetBoxProps & WithTranslation,
  State
> {
  state: State = {
    showPostPicker: false,
  };
  render() {
    const {
      widgetMoveUp,
      widgetMoveDown,
      widgetRemove,
      section,
      zone,
      ...props
    } = this.props;

    const isDeviationUnavailable =
      !props.deviation ||
      (props.deviation.isBlocked && !props.deviation.isMature) || // Mature should show the Dumbo (which supports hidden mature mode)
      props.deviation.isDeleted;

    const emptyStateView = 'large';
    const emptyStateClassName = s['empty-state'];

    return (
      <>
        {this.state.showPostPicker &&
          this.renderPostPicker(this.hidePostPicker)}
        <WidgetBox
          section={section}
          zone={zone}
          moduleId={props.moduleId}
          header={
            <>
              <PremiumFolderDataIcon
                premiumFolderData={props?.deviation?.premiumFolderData}
                size={IconSize.COMPACT}
                className={s['premium-folder-icon']}
              />
              {props.t(props.title || 'widgets.featured_post.header')}
            </>
          }
          headerExtras={this.renderHeaderExtras()}
          autoShowWidgetConfig={true}
          widgetMenuRenderer={this.renderConfigPopup}
          widgetMoveUp={widgetMoveUp}
          widgetMoveDown={widgetMoveDown}
          widgetRemove={widgetRemove}
          isEmpty={!props.deviation}
          emptyView={
            props.isOwner && (
              <EmptyState
                view={emptyStateView}
                className={emptyStateClassName}
                title={props.t('widgets.featured_post.empty.title.owner')}
                subtitle={props.t('widgets.featured_post.empty.subtitle.owner')}
                withButton
                buttonLabel={props.t(
                  'widgets.featured_post.empty.CTALabel.owner'
                )}
                onButtonClick={this.showPostPicker}
              />
            )
          }
        >
          {isDeviationUnavailable && props.isOwner && (
            <EmptyState
              view={emptyStateView}
              className={emptyStateClassName}
              title={props.t('widgets.featured_post.unavailable.title.owner')}
              subtitle={props.t(
                'widgets.featured_post.unavailable.subtitle.owner'
              )}
              withButton
              buttonLabel={props.t(
                'widgets.featured_post.empty.CTALabel.owner'
              )}
              onButtonClick={this.showPostPicker}
            />
          )}
          {isDeviationUnavailable && !props.isOwner && (
            <EmptyState
              view={emptyStateView}
              className={emptyStateClassName}
              title={props.t('widgets.featured_post.unavailable.title.visitor')}
              subtitle={props.t(
                'widgets.featured_post.unavailable.subtitle.visitor'
              )}
            />
          )}
          {!isDeviationUnavailable && <FeaturedPost {...props} hideThumbInfo />}
        </WidgetBox>
      </>
    );
  }

  renderHeaderExtras = () => {
    const { deviation, profileOwnerUser } = this.props;
    if (deviation && !isDeviationAuthor(deviation, profileOwnerUser)) {
      return <FavouritesLabel tooltipUser={profileOwnerUser} />;
    }
  };

  renderConfigPopup = close => {
    const { moduleId, isNewlyAddedModule } = this.props;
    if (isNewlyAddedModule) {
      return this.renderPostPicker(close);
    }
    return (
      <Suspense fallback={<ChunkLoadingPopover />}>
        <EditForm {...this.props} moduleId={moduleId} onClose={close} />
      </Suspense>
    );
  };

  renderPostPicker = (close?) => {
    return (
      <Suspense fallback={<ChunkLoadingPopover />}>
        <PostPicker moduleId={this.props.moduleId} onClose={close} />
      </Suspense>
    );
  };

  showPostPicker = () => {
    this.setState({ showPostPicker: true });
  };
  hidePostPicker = () => {
    this.setState({ showPostPicker: false });
  };
}

export default enhance(FeaturedPostBox) as React.ComponentType<{}>;
